import {
  analyzeOptimization,
  applyOptimizationRecommendations,
  canAutoApplyOptimizationRecommendation,
  type OptimizationAppliedRecommendation,
  type OptimizationRecommendation,
  type OptimizationSkippedRecommendation,
  type OptimizationSeoInput,
} from "@ease/page-builder/services";
import type { PuckComponent } from "@ease/page-builder/ai";

export interface SeoMeta extends OptimizationSeoInput {
  title: string;
  description: string;
  ogImage: string;
  slug: string;
  focusKeyword: string;
  [key: string]: unknown;
}

export interface SeoIssue {
  id: string;
  severity: "error" | "warning" | "info";
  message: string;
  fix: string;
}

export interface SeoResult {
  score: number;
  issues: SeoIssue[];
  schemaOrg: Record<string, unknown>;
  recommendations: OptimizationRecommendation[];
  autoFixableRecommendationIds: string[];
  metaSuggestion: {
    title: string;
    description: string;
  };
}

export interface SeoOptimizationResult {
  meta: SeoMeta;
  components: PuckComponent[];
  applied: OptimizationAppliedRecommendation[];
  skipped: OptimizationSkippedRecommendation[];
  analysis: SeoResult;
}

interface OptimizeSeoOptions {
  includeComponentFixes?: boolean;
}

export type { PuckComponent };

export function analyzeSeo(
  meta: SeoMeta,
  components: PuckComponent[],
): SeoResult {
  const analysis = analyzeOptimization({
    components,
    seo: meta,
  });

  return toSeoResult(analysis);
}

export function optimizeSeoDraft(
  meta: SeoMeta,
  components: PuckComponent[],
  options: OptimizeSeoOptions = {},
): SeoOptimizationResult {
  const analysis = analyzeOptimization({
    components,
    seo: meta,
  });
  const recommendationIds = analysis.recommendations
    .filter((recommendation) => isAutoFixableSeoRecommendation(
      recommendation,
      options.includeComponentFixes === true,
    ))
    .map((recommendation) => recommendation.id);

  if (recommendationIds.length === 0) {
    return {
      meta: normalizeSeoMeta(meta),
      components,
      applied: [],
      skipped: [],
      analysis: toSeoResult(analysis),
    };
  }

  const result = applyOptimizationRecommendations({
    components,
    seo: meta,
    recommendationIds,
  });

  return {
    meta: normalizeSeoMeta(result.seo),
    components: result.components,
    applied: result.applied,
    skipped: result.skipped,
    analysis: toSeoResult(result.analysis),
  };
}

function toSeoResult(
  analysis: ReturnType<typeof analyzeOptimization>,
): SeoResult {
  return {
    score: analysis.seo.score.total,
    issues: analysis.recommendations
      .filter((item) => item.category === "seo")
      .map(toSeoIssue),
    schemaOrg: toSchemaPayload(analysis.seo.schema.schemas),
    recommendations: analysis.recommendations.filter((item) => item.category === "seo"),
    autoFixableRecommendationIds: analysis.recommendations
      .filter((recommendation) => isAutoFixableSeoRecommendation(recommendation, true))
      .map((recommendation) => recommendation.id),
    metaSuggestion: analysis.seo.metaSuggestion,
  };
}

const COMPONENT_FIX_RECOMMENDATION_IDS = new Set(["seo:alt", "seo:headings"]);

function isAutoFixableSeoRecommendation(
  recommendation: OptimizationRecommendation,
  includeComponentFixes: boolean,
): boolean {
  if (recommendation.category !== "seo") {
    return false;
  }
  if (!canAutoApplyOptimizationRecommendation(recommendation.id)) {
    return false;
  }
  if (!includeComponentFixes && COMPONENT_FIX_RECOMMENDATION_IDS.has(recommendation.id)) {
    return false;
  }
  return true;
}

function normalizeSeoMeta(meta: OptimizationSeoInput): SeoMeta {
  return {
    ...meta,
    title: typeof meta.title === "string" ? meta.title : "",
    description: typeof meta.description === "string" ? meta.description : "",
    ogImage: typeof meta.ogImage === "string" ? meta.ogImage : "",
    slug: typeof meta.slug === "string" ? meta.slug : "",
    focusKeyword: typeof meta.focusKeyword === "string" ? meta.focusKeyword : "",
  };
}

function toSeoIssue(recommendation: OptimizationRecommendation): SeoIssue {
  return {
    id: recommendation.id,
    severity: toSeverity(recommendation.impact),
    message: recommendation.description,
    fix: recommendation.action ?? "Review this SEO signal in the builder analysis",
  };
}

function toSeverity(
  impact: OptimizationRecommendation["impact"],
): SeoIssue["severity"] {
  if (impact === "high") {
    return "error";
  }
  if (impact === "medium") {
    return "warning";
  }
  return "info";
}

function toSchemaPayload(
  schemas: Array<Record<string, unknown>>,
): Record<string, unknown> {
  if (schemas.length === 0) {
    return {};
  }

  if (schemas.length === 1) {
    return schemas[0]!;
  }

  return {
    "@context": "https://schema.org",
    "@graph": schemas,
  };
}
